import {
  ActionIcon,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Stack,
  Switch,
  Text as MantineText,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { i18n } from '@lingui/core';
import { FC, useEffect, useState } from 'react';
import {
  IconBallpen,
  IconCheck,
  IconDots,
  IconNote,
  IconSend,
  IconServer,
  IconTrashX,
  IconUser,
  IconX,
} from '@tabler/icons';
import { MailgunDomainDTO, MailOutBoundConfigDTO } from '../../../Types/MailT';
import { GrayBoxV2 } from '../../../Atoms/GrayBox';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { MailS } from '../../../Service/MailS';
import { openScheduleMeetingModal } from '../ContactFormBuilder/ScheduledMettingAlert';
import { isDefined } from '../../../Service/ValidationS';
import ConfigOption from '../../../Atoms/ConfigOption';
import { ThemeText } from '../../../Atoms/ThemeText';
import {
  openDeleteOutboundConfigModal,
  openDomainNotVerifiedModal,
  openMailAccountModal,
  openRevokeGrandModal,
} from './MailAccountModals';
import MailgunDomain from './mailgun/MailgunDomain';

interface Props {
  hasMultipleAccounts: boolean;
  mailAccount: MailOutBoundConfigDTO;
  removeHandler: (cfgId: number) => void;
  editHandler: (cfg: MailOutBoundConfigDTO) => void;
}

const MailAccountPreview: FC<Props> = ({ hasMultipleAccounts, mailAccount, removeHandler, editHandler }) => {
  const {
    id,
    smptHost,
    smptPort,
    description,
    startTls,
    auth,
    login,
    email,
    bcc,
    externalAccountId,
    verified,
    domainName,
    type,
    defaultAccount,
    connectionVerified,
  } = mailAccount;
  const { profileId } = useJwtClaims();
  const [sendingEmail, setSending] = useState(false);
  const [domain, setDomain] = useState<MailgunDomainDTO | null>();
  const sendTestMailAvailable = !domain || (domain && domain.verified);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [updatingDefaultAccount, setUpdatingDefaultAccount] = useState(false);

  useEffect(() => {
    if (domainName) {
      MailS.getDomain(id).then(setDomain);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainName]);

  const sendMail = async () => {
    setSending(true);
    const sent = await MailS.sendTestMail(id);
    setSending(false);
    if (!sent) {
      openScheduleMeetingModal();
    }
  };

  const verifyDomain = async () => {
    setVerifyingDomain(true);
    const resJSON = await MailS.verifyDomain(id);
    setVerifyingDomain(false);
    setDomain(resJSON);
    if (resJSON && resJSON.verified) {
      editHandler({ ...mailAccount, verified: true });
    } else {
      openDomainNotVerifiedModal();
    }
  };

  const toggleDefaultAccount = async (checked: boolean) => {
    setUpdatingDefaultAccount(true);
    const cfg = await MailS.patchMailOutboundConfig(id, { defaultAccount: checked });
    setUpdatingDefaultAccount(false);
    if (cfg) {
      editHandler(cfg);
    }
  };

  return (
    <>
      <GrayBoxV2
        rightSection={
          <Menu shadow="md" width={220} transition="scale-y" position="bottom-end">
            <Menu.Target>
              <ActionIcon>
                <IconDots />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {sendTestMailAvailable && (
                <Menu.Item
                  icon={sendingEmail ? <Loader size="xs" /> : <IconSend size={16} />}
                  disabled={sendingEmail}
                  onClick={sendMail}
                >
                  Test-E-Mail verschicken
                </Menu.Item>
              )}
              <Menu.Item
                icon={<IconBallpen size={16} />}
                onClick={() =>
                  openMailAccountModal({ profileId, mailAccount, onFinish: editHandler, hasMultipleAccounts })
                }
              >
                Bearbeiten
              </Menu.Item>
              <Menu.Item
                color="red"
                icon={<IconTrashX size={16} />}
                onClick={() =>
                  openDeleteOutboundConfigModal({
                    config: mailAccount,
                    onDelete: () => {
                      if (externalAccountId) {
                        openRevokeGrandModal();
                      }
                      removeHandler(id);
                    },
                  })
                }
              >
                Löschen
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        }
      >
        <Group spacing="xs" mb="lg">
          <Tooltip
            label={i18n._(
              connectionVerified
                ? 'settings.mail.connection.verified.tooltip'
                : 'settings.mail.connection.not-verified.tooltip',
            )}
            transition="slide-up"
          >
            <ThemeIcon radius="xl" color={connectionVerified ? '#40d84b' : 'red'} size="sm">
              {connectionVerified ? <IconCheck size={14} /> : <IconX size={14} />}
            </ThemeIcon>
          </Tooltip>
          <MantineText size="lg" weight="bolder">
            {email}
          </MantineText>
        </Group>
        <Stack mt="md" spacing="xs" sx={{ color: 'white' }}>
          {description && (
            <ThemeText>
              <IconNote size={20} style={{ marginRight: 10, marginTop: -3 }} />
              {description}
            </ThemeText>
          )}
          {type === 'SMTP' && !externalAccountId && (
            <ThemeText>
              <IconServer size={20} style={{ marginRight: 10, marginTop: -3 }} />
              {`${smptHost} : ${smptPort}`}
            </ThemeText>
          )}

          {type === 'SMTP' && isDefined(login) && login !== email && (
            <ThemeText>
              <IconUser size={20} style={{ marginRight: 10, marginTop: -3 }} />
              {login}
            </ThemeText>
          )}
          {isDefined(bcc) && (
            <ThemeText weight="bolder" size="xs">
              BCC{' '}
              <MantineText span weight="normal" size="sm" style={{ marginLeft: 10 }}>
                {bcc}
              </MantineText>
            </ThemeText>
          )}
        </Stack>
        {type === 'SMTP' && (
          <Group spacing="xl" mt="md">
            <MantineText>
              <ThemeIcon radius="xl" color={startTls ? 'blue' : 'gray'} size="sm" mr="sm">
                {auth ? <IconCheck size={14} /> : <IconX size={14} />}
              </ThemeIcon>
              StartTLS
            </MantineText>
            <MantineText>
              <ThemeIcon radius="xl" color={auth ? 'blue' : 'gray'} size="sm" mr="sm">
                {auth ? <IconCheck size={14} /> : <IconX size={14} />}
              </ThemeIcon>
              SSL-Verschlüsselt
            </MantineText>
          </Group>
        )}
        <ConfigOption
          my="xl"
          title="Standardkonto"
          description="Dieses Konto wird standardmaessig fuer ausgehende E-Mails verwendet."
          rightSection={
            <Switch
              checked={defaultAccount}
              disabled={updatingDefaultAccount}
              onChange={(e) => toggleDefaultAccount(e.currentTarget.checked)}
            />
          }
        />
      </GrayBoxV2>
      {domain && type === 'SMTP_RELAY' && (
        <>
          <MailgunDomain domain={domain} />
          {!verified && (
            <Center>
              <Button
                size="xs"
                mt="xl"
                onClick={verifyDomain}
                disabled={verifyingDomain}
                leftIcon={verifyingDomain && <Loader size="xs" />}
              >
                Jetzt überprüfen
              </Button>
            </Center>
          )}
        </>
      )}
    </>
  );
};

export default MailAccountPreview;
